import request, { keysToSnakeCase } from "@src/utils/request";

export interface PriceBreak {
  quantity: number;
  price: number;
}

export interface DistributorPricing {
  distributorName: string;
  distributorSlug: string;
  price?: number;
  priceBreaks: PriceBreak[];
  minQuantity?: number;
  availabilityStatus?: string;
  isInStock?: boolean;
  isReturnable?: boolean;
  quantityAvailable?: number;
  pricePerUnit?: number;
  webEligible?: boolean;
  buttonText: string;
  cartButtonDisabled: boolean;
  isConnected?: boolean;
}

export interface DistributorProductImage {
  id: string;
  imageType?: string;
  imageUrl: string;
}

export interface DistributorProduct {
  id: string;
  item_number: string;
  schedule?: string | null;
  distributor: {
    id: string;
    name: string;
    slug: string;
    isPreferred?: boolean;
  };
  stock_level: string;
  availability_status: string;
  manufacturerCode: string;
  isReturnable: boolean;
  quantity_available: number;
  livePricing?: DistributorPricing;
  images?: DistributorProductImage[];
  // Product-level fields for variant display
  productItemName?: string;
  productItemNumber?: string;
  productDescription?: string;
  productManufacturer?: string;
}

export interface ProductDetail {
  id: string;
  itemNumber: string;
  itemName: string;
  manufacturer: {
    id: string;
    name: string;
  };
  manufacturerName?: string;
  description?: string;
  unitOfMeasure: string;
  packageQuantity: number;
  prop65?: string;
  sdsUrl?: string;
  msdsUrl?: string;
  compendiumUrl?: string;
  distributorProducts: DistributorProduct[];
}

export interface ProductGroup {
  id: string;
  name: string;
  brand: string;
  manufacturer: string;
  activeIngredient: string;
  imageUrl?: string | null;
}

export interface VariantMember {
  [key: string]: string | number | undefined;
}

export interface ProductVariantsResponse {
  productGroup: ProductGroup;
  allFilterOptions: {
    [key: string]: Array<string | number>;
  };
  availableFilterOptions: {
    [key: string]: Array<string | number>;
  };
  memberVariants: VariantMember[];
  attributeLabels: {
    [key: string]: string;
  };
  defaultFilters: {
    [key: string]: string | number;
  };
  appliedFilters: {
    [key: string]: string | number;
  };
  pricing: DistributorProduct[];
  variantCount: number;
}

export async function getProductDetail(
  productId: string,
): Promise<{ body: ProductDetail }> {
  return request().get(`/api/v1/products/${productId}/`);
}

export interface SiblingGroup {
  groupId: string;
  canonicalName: string;
  groupAttributes: Record<string, string>;
}

export interface SiblingGroupsResponse {
  groups: SiblingGroup[];
  groupAttributeOptions: Record<string, string[]>;
  attributeDisplayNames: Record<string, string>;
  currentGroupId: string | null;
  currentGroupVariantAttributes: string[];
  isStandalone: boolean;
}

export async function getProductVariants(
  productId: string,
  filters?: Record<string, string | number>,
): Promise<{ body: ProductVariantsResponse }> {
  const req = request().get(`/api/v1/products/${productId}/variants/`);

  // Convert filter keys from camelCase to snake_case for backend
  if (filters) {
    req.query(keysToSnakeCase(filters));
  }

  return req;
}

export async function getSiblingGroups(
  productId: string,
  groupContext?: Record<string, string>,
): Promise<{ body: SiblingGroupsResponse }> {
  const req = request().get(`/api/v1/products/${productId}/sibling-groups/`);
  if (groupContext && Object.keys(groupContext).length > 0) {
    req.query(groupContext);
  }
  return req;
}
